import { z } from 'zod';

// src/schemas/author/schema.ts
var MarkupOptions = /* @__PURE__ */ ((MarkupOptions2) => {
  MarkupOptions2["Italic"] = "style:em";
  MarkupOptions2["Bold"] = "style:strong";
  MarkupOptions2["SuperScript"] = "style:superscript";
  MarkupOptions2["SubScript"] = "style:subscript";
  MarkupOptions2["Highlight"] = "style:highlight";
  MarkupOptions2["Link"] = "link:external";
  MarkupOptions2["LinkInternal"] = "link:internal";
  MarkupOptions2["Symbol"] = "symbol";
  MarkupOptions2["Explanation"] = "explanation";
  return MarkupOptions2;
})(MarkupOptions || {});
var baseSchemaProperties = {
  offset: z.number(),
  length: z.number()
};
var styleMarkupSchema = z.object({
  type: z.nativeEnum({
    ["style:em" /* Italic */]: "style:em" /* Italic */,
    ["style:strong" /* Bold */]: "style:strong" /* Bold */,
    ["style:superscript" /* SuperScript */]: "style:superscript" /* SuperScript */,
    ["style:subscript" /* SubScript */]: "style:subscript" /* SubScript */,
    ["style:highlight" /* Highlight */]: "style:highlight" /* Highlight */
  }),
  ...baseSchemaProperties
});
var linkMarkupSchema = z.object({
  type: z.nativeEnum({
    ["link:internal" /* LinkInternal */]: "link:internal" /* LinkInternal */,
    ["link:external" /* Link */]: "link:external" /* Link */
  }),
  uri: z.string(),
  ...baseSchemaProperties
});
var symbolMarkupSchema = z.object({
  type: z.nativeEnum({
    ["symbol" /* Symbol */]: "symbol" /* Symbol */,
    ["explanation" /* Explanation */]: "explanation" /* Explanation */
  }),
  symbol: z.string(),
  ...baseSchemaProperties
});
var markupSchema = z.union([
  styleMarkupSchema,
  linkMarkupSchema,
  symbolMarkupSchema
]);
var isLinkMarkup = (maybeLinkMarkup) => linkMarkupSchema.safeParse(maybeLinkMarkup).success;
var isSymbolMarkup = (maybeSymbolMarkup) => symbolMarkupSchema.safeParse(maybeSymbolMarkup).success;

// src/schemas/common/capi/text.ts
var textSchema = z.object({
  value: z.string(),
  markup: z.array(markupSchema).optional()
});

// src/schemas/common/capi/block.ts
var ListBlockTypes = /* @__PURE__ */ ((ListBlockTypes2) => {
  ListBlockTypes2["Checked"] = "list:checked";
  ListBlockTypes2["Ordered"] = "list:ordered";
  ListBlockTypes2["Unordered"] = "list:unordered";
  return ListBlockTypes2;
})(ListBlockTypes || {});
var listBlockSchema = z.object({
  blockType: z.nativeEnum(ListBlockTypes),
  items: z.array(textSchema)
});
var TextBlockTypes = /* @__PURE__ */ ((TextBlockTypes2) => {
  TextBlockTypes2["Heading"] = "heading";
  TextBlockTypes2["Heading2"] = "heading:level-2";
  TextBlockTypes2["Quote"] = "quote";
  TextBlockTypes2["Paragraph"] = "paragraph";
  return TextBlockTypes2;
})(TextBlockTypes || {});
var textBlockSchema = z.object({
  blockType: z.nativeEnum(TextBlockTypes),
  text: textSchema
});
var richTextSchema = z.array(
  z.union([listBlockSchema, textBlockSchema])
);
var isTextBlock = (maybeTextBlock) => textBlockSchema.safeParse(maybeTextBlock).success;
var isListBlock = (maybeTextBlock) => listBlockSchema.safeParse(maybeTextBlock).success;
var locationMetadataSchema = z.object({
  location: z.object({
    lat: z.number(),
    lon: z.number()
  }).optional(),
  type: z.literal("geolocation")
});
var organizationMetadataSchema = z.object({
  type: z.enum(["isin", "vat", "ticker"]),
  value: z.string()
});
var websiteMetadataSchema = z.object({
  type: z.literal("website"),
  url: z.string()
});
var baseTagSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  slug: z.string().min(1).regex(/^[a-z0-9-]+$/, "Tag slug seems malformed"),
  enabled: z.boolean().optional(),
  scope: z.enum(["no", "sv"]).optional()
});
var descriptorTagSchema = baseTagSchema.extend({
  type: z.literal("descriptor")
});
var eventTagSchema = baseTagSchema.extend({
  metadata: z.array(websiteMetadataSchema).optional(),
  type: z.literal("event")
});
var locationTagSchema = baseTagSchema.extend({
  metadata: z.array(z.union([locationMetadataSchema, websiteMetadataSchema])).optional(),
  type: z.literal("location")
});
var organizationTagSchema = baseTagSchema.extend({
  metadata: z.array(z.union([organizationMetadataSchema, websiteMetadataSchema])).optional(),
  type: z.literal("organization")
});
var personTagSchema = baseTagSchema.extend({
  metadata: z.array(websiteMetadataSchema).optional(),
  type: z.literal("person")
});
var interestTagSchema = baseTagSchema.extend({
  type: z.literal("interest")
});
var machineTagSchema = baseTagSchema.extend({
  type: z.literal("machine")
});
var tagSchema = z.union([
  descriptorTagSchema,
  eventTagSchema,
  locationTagSchema,
  organizationTagSchema,
  personTagSchema,
  interestTagSchema,
  machineTagSchema
]);
var utcDateTimeStringSchema = z.string();
var changesSchema = z.object({
  created: utcDateTimeStringSchema,
  updated: utcDateTimeStringSchema.optional(),
  firstPublished: utcDateTimeStringSchema,
  published: utcDateTimeStringSchema,
  deleted: utcDateTimeStringSchema.optional()
});
var videoIntervalSchema = z.object({
  start: z.number(),
  end: z.number().optional()
});
var videoSectionSchema = z.object({
  title: z.string(),
  id: z.string(),
  shortPath: z.string().optional(),
  metadata: z.object({
    podcast_author: z.string().optional(),
    podcast_categoryTree: z.string().optional(),
    actingEditor: z.string().optional(),
    afp: z.enum(["true"]).optional(),
    sponsored: z.enum(["true"]).optional()
  }).and(z.record(z.unknown())).optional(),
  parent: z.lazy(() => videoSectionSchema).optional()
});
var videoAssetSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().optional(),
  descriptionFront: z.string().optional(),
  flightTimes: videoIntervalSchema.optional(),
  duration: z.number().optional(),
  size: z.object({
    width: z.number(),
    height: z.number()
  }).optional(),
  assetType: z.enum(["video", "audio"]).optional(),
  streamType: z.string().optional(),
  playback: videoIntervalSchema.nullable().optional(),
  streamUrls: z.object({
    hls: z.string(),
    hds: z.string().optional(),
    mp4: z.string().optional(),
    pseudostreaming: z.array(z.string()).optional()
  }).optional(),
  streamConfiguration: z.object({
    properties: z.array(
      z.enum([
        "tokenSecured",
        "encrypted",
        "geoblocked",
        "playerVerified"
      ])
    ).optional()
  }).optional(),
  cuePoints: z.array(
    z.object({
      timeline: z.number()
    })
  ).optional(),
  sourceFiles: z.array(
    z.object({
      width: z.number(),
      height: z.number(),
      bitrate: z.number(),
      sourceFile: z.string()
    })
  ).optional(),
  subtitles: z.array(
    z.object({
      language: z.string(),
      url: z.string(),
      default: z.boolean().optional()
    })
  ).optional(),
  tags: z.array(tagSchema).optional(),
  originAssetId: z.string().optional(),
  nextAssetId: z.string().optional(),
  access: z.record(z.boolean()).optional(),
  metadata: z.object({
    aspectRatio: z.string().optional(),
    sponsored: z.string().optional()
  }).and(z.record(z.unknown())).optional(),
  section: videoSectionSchema.optional(),
  series: z.object({
    seasonNumber: z.number(),
    episodeNumber: z.number()
  }).optional(),
  showAds: z.boolean().optional(),
  provider: z.string().optional(),
  appnexusVideoAdParams: z.object({
    invCode: z.string(),
    videoOverlayInvCode: z.string(),
    sections: z.string()
  }).optional(),
  titleFront: z.string().optional(),
  displays: z.number().optional(),
  showDisplays: z.boolean().optional()
});

// src/schemas/common/capi/entities.ts
var fetchEntityTypesSchema = z.enum([
  "section",
  "story",
  "tags",
  "authors",
  "video"
]);
var sectionFields = {
  id: z.string(),
  title: z.string(),
  enabled: z.boolean().optional(),
  isVirtual: z.boolean().optional(),
  scope: z.string().optional()
};
var sectionEntitySchema = z.object({
  ...sectionFields,
  parent: z.object({
    ...sectionFields,
    parent: z.object(sectionFields).optional()
  }).optional()
});
var storyEntitySchema = z.object({
  id: z.string(),
  title: z.string(),
  slug: z.string(),
  description: z.string().optional(),
  updated: z.string().optional(),
  enabled: z.boolean().optional(),
  background: z.array(textBlockSchema).optional(),
  scope: z.string().optional()
});
var contactTypeSchema = z.enum([
  "email",
  "web",
  "title",
  "twitter",
  "facebook",
  "signal",
  "phone",
  "agency",
  "department",
  "other"
]);
var authorEntitySchema = z.object({
  title: z.string(),
  id: z.string(),
  slug: z.string(),
  contacts: z.array(
    z.object({
      type: contactTypeSchema.optional(),
      value: z.string()
    })
  ),
  image: z.string().optional(),
  description: z.string().optional(),
  imageAsset: z.object({
    id: z.string()
  }).optional(),
  enabled: z.boolean().optional(),
  scope: z.string().optional()
});
var videoEntitySchema = z.object({
  id: z.string(),
  changes: changesSchema,
  videoAsset: videoAssetSchema,
  title: textSchema,
  description: textSchema.optional(),
  state: z.string(),
  imageAsset: z.object({
    id: z.string(),
    main: z.string(),
    front: z.string().optional(),
    snapshots: z.string(),
    featured: z.string().optional()
  }),
  scope: z.string()
});
var entityMapSchema = z.object({
  section: sectionEntitySchema,
  story: storyEntitySchema,
  tags: tagSchema,
  authors: authorEntitySchema,
  video: videoEntitySchema
});
var paywallTypeSchema = z.enum([
  "",
  "subscription",
  "metered,subscription",
  "login",
  "login-metered,subscription"
]);
var restrictionTypeSchema = z.union([
  z.enum([
    "automatic",
    "open",
    "closed",
    "metered",
    "true",
    "plus",
    "subscription",
    "login",
    "login-metered",
    "default"
  ]),
  z.boolean()
]);
var accessControlSchema = z.object({
  restrictions: z.array(restrictionTypeSchema),
  control: z.object({
    aid: z.string(),
    productIds: z.string(),
    paywallType: paywallTypeSchema
  }).optional()
});
var articleCharacteristicsSchema = z.object({
  lifetime: z.number(),
  hotness: z.number()
});
var baseComponentPresentationPropertiesSchema = z.object({
  intention: z.string().optional(),
  makeAnchor: z.string().optional(),
  theme: z.string().optional(),
  withoutMargin: z.boolean().optional(),
  addLayout: z.string().optional(),
  device: z.enum(["mobile", "desktop", "tablet", "default"]).optional(),
  stickyMenuItemTitle: z.string().optional(),
  stickyMenuItemPosition: z.string().optional(),
  seoType: z.string().optional(),
  richType: z.string().optional(),
  style: z.string().optional(),
  tag: z.string().optional(),
  telephone: z.string().optional(),
  number: z.string().optional(),
  description: z.string().optional(),
  altText: z.string().optional(),
  videoId: z.string().optional(),
  variant: z.string().optional(),
  groupStart: z.string().optional(),
  groupEnd: z.string().optional(),
  imagePreset: z.string().optional(),
  targetUrl: z.string().optional(),
  bylinePrefix: z.string().optional(),
  sponsorUrl: z.string().optional(),
  autoplay: z.string().optional(),
  nextVidStart: z.number().optional(),
  startMode: z.string().optional(),
  loop: z.boolean().optional(),
  cta: z.string().optional(),
  withMuteControl: z.string().optional(),
  withProgressBar: z.string().optional(),
  withScrollDownButton: z.string().optional(),
  mediaPosition: z.string().optional(),
  imageAspectRatio: z.string().optional(),
  image: z.string().optional(),
  floatingTopMargin: z.number().optional(),
  ssr: z.string().optional()
});
var baseComponentSchema = z.object({
  subtype: z.string().optional(),
  presentationProperties: baseComponentPresentationPropertiesSchema.optional()
});
var articleComponentSchema = baseComponentSchema.extend({
  type: z.literal("article"),
  articleId: z.string(),
  linkTitle: z.string().optional()
});
var breakpointComponentSchema = baseComponentSchema.extend({
  type: z.literal("breakpoint")
});
var correctionComponentSchema = baseComponentSchema.extend({
  type: z.literal("correction"),
  paragraphs: richTextSchema
});
var factComponentSchema = baseComponentSchema.extend({
  type: z.literal("fact"),
  title: textSchema.optional(),
  paragraphs: richTextSchema.optional()
});
var hotspotSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number()
});
var imageAssetSchema = z.object({
  id: z.string(),
  main: z.string().optional(),
  front: z.string().optional(),
  snapshots: z.string().optional(),
  featured: z.string().optional(),
  size: z.object({
    width: z.number(),
    height: z.number()
  }).optional(),
  hotspot: hotspotSchema.optional()
});

// src/schemas/common/capi/article/components/image.ts
var imageComponentSchema = baseComponentSchema.extend({
  type: z.literal("image"),
  alternateText: textSchema.optional(),
  byline: z.object({ title: z.string() }).optional(),
  sourceId: z.string().optional(),
  characteristics: z.object({
    figure: z.boolean().optional(),
    sensitive: z.boolean().optional(),
    significance: z.number().optional()
  }).optional(),
  caption: textSchema.optional(),
  imageAsset: imageAssetSchema,
  mediaFit: z.enum(["contain", "cover"]).optional(),
  placement: z.literal("aside").optional()
});
var videoComponentSchema = baseComponentSchema.extend({
  type: z.literal("video"),
  videoAsset: videoAssetSchema,
  imageAsset: z.object({
    id: z.string(),
    main: z.string().optional()
  }),
  title: textSchema.optional(),
  description: textSchema.optional(),
  lazyConfig: z.object({ enabled: z.boolean().optional() }).optional(),
  characteristics: z.object({
    significance: z.number().optional()
  }).optional(),
  metadata: z.object({
    aspectRatio: z.number().optional(),
    exportedToAbse: z.number().optional(),
    preview: z.string().optional(),
    tagsSuggestions: z.array(z.string()).optional(),
    timedTranscript: z.string().optional(),
    transcript: z.string().optional(),
    isPodcast: z.string().optional()
  }).and(z.record(z.unknown())).optional(),
  changes: changesSchema,
  provider: z.object({
    service: z.string(),
    id: z.string()
  }),
  hotspot: hotspotSchema.optional(),
  autoplay: z.boolean().optional(),
  withProgressBar: z.boolean().optional(),
  withMuteControl: z.boolean().optional(),
  mediaFit: z.enum(["contain", "cover"]).optional()
});

// src/schemas/common/capi/article/components/gallery.ts
var galleryComponentSchema = baseComponentSchema.extend({
  type: z.literal("gallery"),
  components: z.array(z.union([imageComponentSchema, videoComponentSchema]))
});
var gradeComponentSchema = baseComponentSchema.extend({
  type: z.literal("grade"),
  grade: z.number().min(0).max(1)
});
var integrationComponentSchema = baseComponentSchema.extend({
  type: z.literal("deprecated-integration"),
  url: z.string()
});
var mapPointSchema = z.object({
  lat: z.number(),
  lon: z.number()
});
var mapComponentSchema = baseComponentSchema.extend({
  type: z.literal("map"),
  zoom: z.number(),
  location: mapPointSchema,
  bounds: z.object({
    sw: mapPointSchema,
    ne: mapPointSchema
  })
});
var quoteComponentSchema = baseComponentSchema.extend({
  type: z.literal("quote"),
  quote: textSchema,
  source: textSchema.optional(),
  context: textSchema.optional()
});
var subheadingComponentSchema = baseComponentSchema.extend({
  type: z.literal("subheading"),
  text: textSchema
});
var tableComponentSchema = baseComponentSchema.extend({
  type: z.literal("table"),
  rows: z.array(z.array(z.string().nullable())),
  headers: z.object({
    rows: z.array(z.number()),
    columns: z.array(z.number())
  })
});
var textComponentSchema = baseComponentSchema.extend({
  type: z.literal("text"),
  paragraphs: richTextSchema
});
var leadTextComponentSchema = textComponentSchema.extend({
  subtype: z.literal("lead")
});
var urlComponentDataSchema = z.object({
  type: z.string().optional(),
  source: z.string().optional(),
  canonicalUrl: z.string().optional(),
  image: z.string().optional(),
  customProperties: baseComponentPresentationPropertiesSchema.optional()
});
var urlComponentSchema = baseComponentSchema.extend({
  type: z.literal("url"),
  url: z.string(),
  title: textSchema.optional(),
  label: z.string().optional(),
  data: urlComponentDataSchema.optional()
});

// src/schemas/common/capi/article/components/component.ts
var componentsWithoutArticleSchema = [
  breakpointComponentSchema,
  correctionComponentSchema,
  factComponentSchema,
  galleryComponentSchema,
  gradeComponentSchema,
  imageComponentSchema,
  integrationComponentSchema,
  mapComponentSchema,
  quoteComponentSchema,
  subheadingComponentSchema,
  tableComponentSchema,
  textComponentSchema,
  urlComponentSchema,
  videoComponentSchema
];
var listComponentSchema = baseComponentSchema.extend({
  type: z.literal("list"),
  components: z.array(
    z.union([...componentsWithoutArticleSchema, articleComponentSchema])
  )
});
var componentSchema = z.union([
  ...componentsWithoutArticleSchema,
  listComponentSchema,
  articleComponentSchema
]);
var questionPresentationPropertiesSchema = z.object({
  richType: z.literal("question"),
  expirationDate: z.string().optional(),
  questionId: z.string().optional(),
  questionMode: z.string().optional(),
  requiresLogin: z.string().optional()
});
var questionComponentSchema = listComponentSchema.extend({
  presentationProperties: questionPresentationPropertiesSchema.optional()
});
var articleEnrichmentsSchema = z.object({
  "article-comments": z.object({
    count: z.number()
  }).optional(),
  "ad-segments": z.object({
    segments: z.array(z.object({ id: z.string() })).optional()
  }).optional(),
  "text-to-speech": z.object({
    videoAsset: z.object({
      id: z.string()
    }),
    provider: z.object({
      id: z.string(),
      service: z.string()
    })
  }).optional()
}).and(z.record(z.unknown()));
var knownArticlePresentationPropertiesSchema = z.object({
  introType: z.string().optional(),
  introAllowedItems: z.string().optional(),
  introMode: z.string().optional(),
  introTextPosition: z.string().optional(),
  introEffect: z.string().optional(),
  introGradientMode: z.string().optional(),
  introWithoutMargin: z.boolean().optional(),
  introTextShadow: z.string().optional(),
  hiddenTitle: z.union([z.boolean(), z.string()]).optional(),
  removeAds: z.boolean().optional(),
  disableAds: z.boolean().optional(),
  navigation: z.string().optional(),
  cardOnlyStory: z.boolean().optional()
});
var teaserVariantSchema = z.object({
  id: z.string(),
  weight: z.number().optional(),
  title: textSchema.optional(),
  deck: textSchema.optional(),
  imageAsset: imageAssetSchema.optional()
});
var teaserVariantsConfigurationSchema = z.record(
  z.string(),
  z.array(teaserVariantSchema)
);

// src/schemas/common/capi/article/promotion-content.ts
var promotionContentSchema = z.object({
  title: textSchema.optional(),
  description: textSchema.optional(),
  imageAsset: imageAssetSchema.optional(),
  presentationProperties: knownArticlePresentationPropertiesSchema.optional(),
  slug: z.string().optional(),
  context: teaserVariantsConfigurationSchema.optional()
});
var originatorSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("title"),
    title: z.string()
  }),
  z.object({
    type: z.literal("id"),
    ...authorEntitySchema.shape
  })
]);
var sourceSchema = z.object({
  type: z.string().optional(),
  id: z.string(),
  title: z.string().optional(),
  authors: z.array(z.string()).optional(),
  originators: z.array(originatorSchema).optional(),
  reference: z.string().optional(),
  contracts: z.array(z.string()).optional()
});

// src/schemas/common/capi/article/article-base.ts
var articleBaseSchema = z.object({
  access: accessControlSchema,
  authors: z.array(authorEntitySchema),
  characteristics: articleCharacteristicsSchema,
  changes: changesSchema,
  comments: z.string().optional(),
  components: z.array(componentSchema),
  contentType: z.enum(["podcast", "video", "audio", "liveblogpost", "event", ""]).default(""),
  customProperties: z.object({}).catchall(z.unknown()),
  enrichments: articleEnrichmentsSchema.optional(),
  external: z.boolean().optional(),
  id: z.string(),
  immediateSource: sourceSchema.optional(),
  isAutoUpdating: z.boolean().optional(),
  language: z.string(),
  newsroom: z.string(),
  presentationProperties: knownArticlePresentationPropertiesSchema.optional(),
  promotionContent: promotionContentSchema,
  schemaType: z.literal("article"),
  schemaVersion: z.literal(5),
  section: sectionEntitySchema,
  sources: z.array(sourceSchema).optional(),
  source: sourceSchema.optional(),
  state: z.enum(["draft", "done", "published"]),
  story: storyEntitySchema.optional(),
  tags: z.array(tagSchema),
  title: textSchema,
  version: z.object({
    sequenceNo: z.number(),
    time: z.string()
  })
});
var contactDetailSchema = z.object({
  type: contactTypeSchema,
  value: z.string().min(1)
});
var authorItemSchema = z.object({
  id: z.string(),
  locale: z.string(),
  newsroom: z.string(),
  type: z.literal("author"),
  model: authorEntitySchema
});
var listItemTypeSchema = z.enum([
  "article",
  "author",
  "story",
  "tag",
  "section",
  "video",
  "list"
]);
var hitsInfoSchema = z.object({
  total: z.number(),
  from: z.number(),
  size: z.number()
});
var listItemSchema = z.object({
  newsroom: z.string().optional(),
  locale: z.string(),
  id: z.string(),
  type: listItemTypeSchema,
  properties: z.record(z.unknown()).optional()
});
var listSchema = z.object({
  title: z.string().optional(),
  properties: z.record(z.unknown()).optional(),
  layouts: z.union([z.string(), z.array(z.string())]).optional(),
  items: z.array(listItemSchema).optional()
});
var articleListItemSchema = z.object({
  newsroom: z.string(),
  locale: z.string(),
  id: z.string(),
  type: z.literal("article"),
  properties: z.object({
    viewCount: z.number().optional(),
    pageTitle: z.string().optional()
  }).optional()
});
var tagTypesSchema = z.enum([
  "descriptor",
  "organization",
  "person",
  "event",
  "location",
  "machine",
  "interest"
]);
var tagItemSchema = z.object({
  id: z.string(),
  locale: z.string(),
  type: z.literal("tag"),
  model: z.object({
    id: z.string(),
    title: z.string(),
    type: tagTypesSchema,
    enabled: z.boolean(),
    slug: z.string()
  })
});
var storyItemSchema = z.object({
  id: z.string(),
  locale: z.string(),
  newsroom: z.string(),
  type: z.literal("story"),
  model: z.object({
    id: z.string(),
    title: z.string(),
    enabled: z.boolean(),
    slug: z.string()
  })
});
var sectionItemSchema = z.object({
  id: z.string(),
  locale: z.string(),
  newsroom: z.string(),
  type: z.literal("section"),
  model: z.object({
    id: z.string(),
    title: z.string(),
    enabled: z.boolean(),
    parentId: z.string()
  })
});
var aggregatedItemSchema = z.object({
  id: z.string(),
  docCount: z.number()
});
var sortFieldSchema = z.enum([
  "score",
  "published",
  "firstpublished",
  "updated",
  "published_asc"
]);
var dateRangeFieldSchema = z.enum([
  "published",
  "firstPublished",
  "updated"
]);
var characteristicsRangeSchema = z.string().regex(/^\d+\.\.\d+$/);
var articleTemplateSchema = z.enum([
  "automatic",
  "news",
  "review",
  "feature"
]);
var colorPaletteSchema = z.enum([
  "white",
  "pink",
  "green",
  "blue",
  "yellow"
]);
var gradeScoreSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6)
]);
var gradeSchema = z.object({
  name: z.string(),
  value: gradeScoreSchema.optional()
});
var gradesSchema = z.array(gradeSchema);
var gradeWithScoreSchema = z.object({
  name: z.string(),
  value: gradeScoreSchema
});
var isGradeScore = (maybeGradeScore) => gradeScoreSchema.safeParse(maybeGradeScore).success;
var isGradeWithScore = (maybeGradeWithScore) => gradeWithScoreSchema.safeParse(maybeGradeWithScore).success;

// src/schemas/common/frapi/legacy/capi-article.ts
var priceLevelSchema = z.enum(["low", "medium", "high", "none"]);
var specificPriceLevelSchema = z.enum(["low", "medium", "high"]);
var legacyCustomPropertiesSchema = z.object({
  articleTemplate: articleTemplateSchema.optional(),
  breaking: z.boolean().nullable().optional(),
  comments: z.union([z.string(), z.boolean()]).optional(),
  darkSkin: z.boolean().optional(),
  doNotBundle: z.boolean().optional(),
  feature: z.boolean().optional(),
  frontPageCardSize: z.enum(["small", "medium", "large"]).optional(),
  frontPageDescription: z.string().optional(),
  frontPageLeadText: z.string().optional(),
  frontPageImage: z.object({
    id: z.string()
  }).optional(),
  frontPageMMIntegration: z.string().optional(),
  frontPageSummary: richTextSchema.optional(),
  frontPageTitle: z.string().optional(),
  grade: gradeScoreSchema.nullable().optional(),
  grade_value: gradeScoreSchema.optional(),
  grade_menu: gradeScoreSchema.optional(),
  grade_service: gradeScoreSchema.optional(),
  grade_food: gradeScoreSchema.optional(),
  googleMapsLink: z.string().optional(),
  priceLevel: priceLevelSchema.optional(),
  restaurantAddress: z.string().optional(),
  restaurant_url: z.string().optional(),
  grade_environment: gradeScoreSchema.optional(),
  hideAuthorImage: z.union([z.boolean(), z.literal("false"), z.literal("true")]).optional(),
  highlightedComments: z.boolean().optional(),
  liveBlog: z.boolean().optional(),
  presentation: z.string().optional(),
  seriesHeader: z.string().optional(),
  seriesHeaderLocation: z.string().optional(),
  audioFile: z.string().optional(),
  teaserColorPalette: colorPaletteSchema.optional(),
  frontPageKicker: z.string().optional(),
  fullWidthTeaser: z.boolean().optional(),
  teaserWithIllustration: z.boolean().optional(),
  numbered: z.boolean().optional(),
  topStripeLargeImage: z.boolean().nullable().optional(),
  socialTitle: z.string().optional(),
  socialImage: imageAssetSchema.optional(),
  pageTitle: z.string().optional(),
  metaDescription: z.string().optional(),
  showTipsUs: z.boolean().nullable().optional(),
  paywall: restrictionTypeSchema.optional(),
  ntbAwayClubId: z.string().optional(),
  ntbAwayClubName: z.string().optional(),
  ntbAwayScore90: z.number().optional(),
  ntbAwayTeamId: z.string().optional(),
  ntbAwayTeamName: z.string().optional(),
  ntbHomeClubId: z.string().optional(),
  ntbHomeClubName: z.string().optional(),
  ntbHomeScore90: z.number().optional(),
  ntbHomeTeamId: z.string().optional(),
  ntbHomeTeamName: z.string().optional(),
  ntbMatchDate: z.string().optional(),
  ntbMatchId: z.string().optional(),
  ntbStageFullName: z.number().optional(),
  ntbStageName: z.string().optional(),
  ntbStageGroupName: z.string().optional(),
  ntbTournamentName: z.string().optional(),
  ntbTournamentLevel: z.string().optional(),
  ntbParticipatingClubs: z.array(z.string()).optional(),
  ntbParticipatingTeams: z.array(z.string()).optional(),
  ntbStageId: z.string().optional(),
  ntbStageTypeId: z.string().optional(),
  showTeaserAudioPlayer: z.boolean().optional()
});
var legacyPromotionPropertiesSchema = z.object({
  title: textSchema.optional(),
  description: textSchema.optional(),
  imageAsset: imageAssetSchema.optional(),
  context: teaserVariantsConfigurationSchema.optional(),
  slug: z.string().optional()
}).merge(knownArticlePresentationPropertiesSchema);
var legacyPresentationPropertiesSchema = z.object({
  promotionType: z.string().optional(),
  videoFrontId: z.string().optional(),
  videoFrontVendor: z.string().optional(),
  commentsContentType: z.string().optional(),
  reviewTitle: z.string().optional(),
  frontpageGallery: z.boolean().optional()
}).merge(knownArticlePresentationPropertiesSchema);
var legacyCapiArticleSchema = articleBaseSchema.omit({
  customProperties: true,
  promotionContent: true,
  presentationProperties: true
}).merge(
  z.object({
    customProperties: legacyCustomPropertiesSchema,
    promotionContent: promotionContentSchema.omit({
      presentationProperties: true
    }).merge(
      z.object({
        presentationProperties: legacyPromotionPropertiesSchema.optional()
      })
    ),
    presentationProperties: legacyPresentationPropertiesSchema.optional()
  })
);
var blenderArticleItemSchema = z.object({
  id: z.string(),
  type: z.literal("article"),
  src: z.string().optional(),
  // the list can be extended by editors at any time, some possible values are 'b2b', 'b2c', 'top', 'jm-a', 'jm-l', 'bundle', 'bolig', 'boligbed', 'collab'
  r: z.number().optional(),
  i: z.number().optional(),
  p: z.number().optional()
});
var blenderBundleItemSchema = z.object({
  id: z.string(),
  type: z.literal("bundle"),
  items: z.array(blenderArticleItemSchema),
  properties: z.object({
    subtype: z.literal("story")
  })
});
var blenderItemSchema = z.discriminatedUnion("type", [
  blenderArticleItemSchema,
  blenderBundleItemSchema
]);
var experimentSchema = z.object({
  "@id": z.string(),
  id: z.string(),
  platform: z.string(),
  variant: z.string(),
  custom: z.object({
    testId: z.string()
  }).optional(),
  name: z.string()
});
var ntbMetadataSchema = z.object({
  ntbAwayClubId: z.string().optional(),
  ntbAwayClubName: z.string().optional(),
  ntbAwayScore90: z.number().optional(),
  ntbAwayTeamId: z.string().optional(),
  ntbAwayTeamName: z.string().optional(),
  ntbHomeClubId: z.string().optional(),
  ntbHomeClubName: z.string().optional(),
  ntbHomeScore90: z.number().optional(),
  ntbHomeTeamId: z.string().optional(),
  ntbHomeTeamName: z.string().optional(),
  ntbMatchDate: z.string().optional(),
  ntbMatchId: z.string().optional(),
  ntbParticipatingClubs: z.array(z.string()).optional(),
  ntbParticipatingTeams: z.array(z.string()).optional(),
  ntbStageId: z.string().optional(),
  ntbStageTypeId: z.string().optional(),
  ntbStageFullName: z.string().optional(),
  ntbStageName: z.string().optional(),
  ntbStageGroupName: z.string().optional(),
  ntbTournamentName: z.string().optional(),
  ntbTournamentLevel: z.string().optional(),
  ntbTournamentId: z.string().optional()
});
var frontpageGalleryAssetSchema = z.object({
  frontpageGalleryEnabled: z.boolean(),
  frontpageGalleryAssets: z.array(imageComponentSchema)
});
var svpVideoAssetSchema = z.object({
  svpImage: z.string(),
  svpVideoPreview: z.string(),
  svpVideo: z.boolean()
});

// src/schemas/common/frapi/common/custom-properties.ts
var commonCustomPropertiesSchema = z.object({
  articleComments: z.object({
    count: z.number()
  }).optional(),
  articleTitle: textSchema.optional(),
  audioFile: z.string().optional(),
  breaking: z.boolean().nullable().optional(),
  commentsEnabled: z.boolean().optional(),
  darkSkin: z.boolean().optional(),
  doNotBundle: z.boolean().optional(),
  feature: z.boolean().optional(),
  frontPageCardSize: z.enum(["small", "medium", "large"]).optional(),
  frontPageDescription: z.string().optional(),
  frontPageMMIntegration: z.string().optional(),
  frontPageSummary: richTextSchema.optional(),
  grade: gradeScoreSchema.nullable().optional(),
  hideAuthorImage: z.boolean().optional(),
  highlightedCommentQuestion: z.boolean().optional(),
  highlightedComments: z.boolean().optional(),
  id: z.string().optional(),
  immediateArticleId: z.string().optional(),
  liveBlog: z.boolean().optional(),
  ntbMetadata: ntbMetadataSchema.optional(),
  parentSection: z.string().optional(),
  podcastPremium: z.boolean().optional(),
  podcastTeaser: z.boolean().optional(),
  presentationType: z.string().optional(),
  relevantTag: z.string().optional(),
  section: z.string(),
  seriesHeader: z.string().optional(),
  seriesHeaderLocation: z.string().optional(),
  sourceId: z.string().optional(),
  teaserColorPalette: z.enum(["white", "pink", "green", "blue", "yellow"]).optional(),
  tipUs: z.boolean().nullable().optional(),
  topStripeLargeImage: z.boolean().nullable().optional(),
  reviewTitle: z.string().optional()
}).and(svpVideoAssetSchema.partial()).and(frontpageGalleryAssetSchema.partial());
var imageSchema = z.object({
  type: z.string(),
  imageAsset: z.object({
    id: z.string()
  })
});
var imageWithUrlSchema = imageSchema.extend({
  imageAsset: z.object({
    id: z.string(),
    url: z.string()
  })
});
var pulseTrackingSchema = z.object({
  src: z.string().optional()
});

// src/schemas/common/frapi/common/base-teaser.ts
var baseTeaserSchema = blenderArticleItemSchema.extend({
  type: z.literal("teaser"),
  id: z.string(),
  target: z.object({
    type: z.enum(["link:internal", "link:external"]),
    uri: z.string(),
    expandedUri: z.string()
  }),
  kicker: textSchema.optional(),
  title: textSchema,
  components: z.array(componentSchema).optional(),
  enrichments: articleEnrichmentsSchema.optional(),
  standfirst: textSchema.optional(),
  label: textSchema.optional(),
  image: imageSchema.optional(),
  imagePosition: z.string(),
  timestamp: z.string().optional(),
  changes: changesSchema,
  characteristics: z.object({
    lifetime: z.number(),
    hotness: z.number()
  }),
  clientProperties: z.object({
    language: z.string()
  }),
  customProperties: commonCustomPropertiesSchema,
  source: z.record(z.string()).optional(),
  immediateSource: z.record(z.string()).optional(),
  tags: z.array(tagSchema).optional(),
  access: accessControlSchema.optional(),
  authors: z.array(authorEntitySchema).optional(),
  pulse: pulseTrackingSchema.optional(),
  story: storyEntitySchema.optional(),
  variants: teaserVariantsConfigurationSchema.optional(),
  experiments: z.array(experimentSchema).default([])
});

// src/schemas/common/frapi/ap/teaser.ts
var apTeaserSchema = baseTeaserSchema.extend({
  showTeaserAudioPlayer: z.boolean().optional()
});
var benefitsPresentationPropertiesSchema = z.object({
  knapp: z.string().optional(),
  promotekst: z.string().optional(),
  ekstra: z.string().optional(),
  expirationDate: z.string().optional()
}).merge(knownArticlePresentationPropertiesSchema);
var benefitsBaseSchema = z.object({
  id: z.string(),
  title: z.string(),
  slug: z.string(),
  section: sectionEntitySchema,
  tags: z.array(tagSchema),
  socialMediaTitle: z.string().optional(),
  socialMediaImage: imageAssetSchema.nullable().optional(),
  description: z.string().optional(),
  extLink: z.string().optional(),
  articleType: z.enum(["default", "competition"]),
  hotness: z.number().int().min(10).max(100).multipleOf(10).optional()
});

// src/schemas/common/frapi/benefits/shared/promo.ts
var benefitsPromoSchema = benefitsBaseSchema.extend({
  published: z.string().optional(),
  updated: z.string().optional(),
  lead: leadTextComponentSchema.optional(),
  mainImage: imageAssetSchema.optional(),
  metaDescription: z.string().optional(),
  components: z.array(componentSchema).optional(),
  wordCount: z.number().optional(),
  authors: z.array(authorEntitySchema).optional()
});

// src/schemas/common/frapi/benefits/shared/article.ts
var benefitsArticleSchema = benefitsPromoSchema.extend({
  components: z.array(componentSchema),
  access: accessControlSchema,
  wordCount: z.number(),
  presentationProperties: benefitsPresentationPropertiesSchema.optional()
});
var benefitsTeaserSchema = benefitsBaseSchema.extend({
  type: z.string(),
  src: z.string().optional(),
  presentationProperties: benefitsPresentationPropertiesSchema.optional(),
  grade: z.number().int().min(10).max(100).multipleOf(10).optional(),
  imageId: z.string().optional(),
  image: imageAssetSchema.optional(),
  tables: z.array(tableComponentSchema).optional(),
  promotionContent: promotionContentSchema.optional()
});

// src/schemas/common/frapi/benefits/ap-benefits/teaser.ts
var apBenefitsTeaserSchema = benefitsTeaserSchema;

// src/schemas/common/frapi/benefits/bt-benefits/teaser.ts
var btBenefitsTeaserSchema = benefitsTeaserSchema;

// src/schemas/common/frapi/benefits/bt-benefits/search.ts
var benefitsSearchTeaserSchema = btBenefitsTeaserSchema;

// src/schemas/common/frapi/benefits/sa-benefits/teaser.ts
var saBenefitsTeaserSchema = benefitsTeaserSchema;
var benefitsCustomPropertiesSchema = z.object({
  pageTitle: z.string().optional(),
  socialTitle: z.string().optional(),
  socialImage: imageAssetSchema.optional(),
  frontPageImage: imageAssetSchema.optional(),
  teaserWithIllustration: z.boolean().optional(),
  metaDescription: z.string().optional(),
  article_type: z.enum(["default", "competition"]).optional()
});
var benefitsCapiArticleSchema = articleBaseSchema.omit({
  customProperties: true,
  presentationProperties: true
}).merge(
  z.object({
    customProperties: benefitsCustomPropertiesSchema,
    presentationProperties: benefitsPresentationPropertiesSchema.optional()
  })
);
var invitererPropsSchema = z.object({
  time: z.string().optional(),
  location: z.string().optional(),
  description: z.string().optional(),
  price: z.string().optional(),
  url: z.string().optional(),
  label: z.string().optional(),
  buttonText: z.string().optional()
});

// src/schemas/common/frapi/bt/capi-article.ts
var btCustomPropertiesSchema = z.object({
  btInvitererProps: invitererPropsSchema.optional(),
  reviewTitle: z.string().optional(),
  frontPageLeadText: z.string().optional(),
  frontPageTitle: z.string().optional(),
  frontPageImage: imageAssetSchema.optional(),
  showArticleSummary: z.boolean().optional(),
  paywall: z.string().optional(),
  presentation: z.string().optional(),
  comments: z.string().optional(),
  frontPageCardSize: z.enum(["small", "medium", "large"]).optional(),
  grade: gradeScoreSchema.nullable().optional(),
  darkSkin: z.boolean().optional(),
  breaking: z.boolean().nullable().optional(),
  feature: z.boolean().optional(),
  liveBlog: z.boolean().optional(),
  blockSearchEngineIndexing: z.boolean().optional(),
  highlightedComments: z.boolean().optional(),
  showAds: z.boolean().optional(),
  toPrint: z.boolean().optional(),
  showTipsUs: z.boolean().nullable().optional()
});
var btPromotionPropertiesSchema = z.object({
  googleTitle: z.string(),
  metaDescription: z.string(),
  salesTitle: z.string().optional()
}).merge(knownArticlePresentationPropertiesSchema);
var btPresentationPropertiesSchema = z.object({
  reviewTitle: z.string().optional()
}).merge(invitererPropsSchema).merge(knownArticlePresentationPropertiesSchema);
var btCapiArticleSchema = articleBaseSchema.omit({
  customProperties: true,
  promotionContent: true,
  presentationProperties: true
}).merge(
  z.object({
    customProperties: btCustomPropertiesSchema,
    promotionContent: promotionContentSchema.omit({
      presentationProperties: true
    }).merge(
      z.object({
        presentationProperties: btPromotionPropertiesSchema.optional()
      })
    ),
    presentationProperties: btPresentationPropertiesSchema.optional()
  })
);

// src/schemas/common/frapi/bt/teaser.ts
var btTeaserSchema = baseTeaserSchema.extend({
  image: imageWithUrlSchema.optional(),
  customProperties: btCustomPropertiesSchema
});
var curateContextSchema = z.enum([
  "frontpage",
  "sport",
  "recirculation",
  "lyd"
]);
var blenderPersonalizationSchema = z.object({
  removeOnRead: z.number(),
  removeOnImpressed: z.number(),
  personalizedResults: z.number()
});
var blenderPropertiesSchema = z.object({
  id: z.string(),
  name: z.string(),
  version: z.number(),
  context: z.string().optional(),
  experiment: experimentSchema.optional()
});
var blenderResponseSchema = z.object({
  items: z.array(blenderItemSchema),
  properties: blenderPropertiesSchema,
  personalization: blenderPersonalizationSchema
});
var textToSpeechEnrichmentSchema = z.object({
  videoAsset: z.object({
    id: z.string()
  }),
  provider: z.object({
    id: z.string(),
    service: z.string()
  })
});
var identifiableSchema = z.object({
  id: z.string()
});
var bundleSchema = (itemSchema) => z.object({
  id: z.string(),
  type: z.literal("bundle"),
  items: z.array(itemSchema),
  properties: z.object({
    subtype: z.string()
  })
});
var listWithBundlesSchema = (itemSchema) => z.array(z.union([itemSchema, bundleSchema(itemSchema)]));
var embedDataSchema = z.object({
  html: z.string().optional(),
  iframeUrl: z.string().optional(),
  url: z.string().optional(),
  aspectRatio: z.number().optional(),
  height: z.number().optional(),
  width: z.number().optional()
});
var embedResolverResponseSchema = z.object({
  type: z.string().optional(),
  source: z.string().optional(),
  embed: embedDataSchema.optional(),
  customProperties: z.record(z.unknown()).optional(),
  image: z.string().optional(),
  canonicalUrl: z.string().optional()
});
var imageUrlSchema = z.object({
  url: z.string(),
  width: z.number(),
  height: z.number()
});

// src/schemas/common/frapi/iris/image-asset-with-url.ts
var imageAssetWithUrlsSchema = z.union([
  z.object({
    cropFormat: z.string(),
    id: z.string(),
    size: z.object({
      width: z.number(),
      height: z.number()
    }).optional(),
    hotspot: z.object({
      x: z.number(),
      y: z.number(),
      width: z.number(),
      height: z.number()
    }).optional(),
    urls: z.array(imageUrlSchema)
  }),
  z.undefined()
]);
var irisTeaserSchema = z.object({
  id: z.string(),
  type: z.string(),
  subtype: z.string(),
  layout: z.string(),
  target: z.object({
    type: z.string(),
    uri: z.string(),
    expandedUri: z.string()
  }),
  title: z.object({
    value: z.string()
  }),
  standfirst: z.object({
    value: z.string()
  }).optional(),
  label: z.object({
    value: z.string()
  }),
  presentation: z.string(),
  image: z.object({
    imageAsset: imageAssetWithUrlsSchema,
    type: z.string()
  }).optional(),
  imagePosition: z.string(),
  timestamp: z.string(),
  changes: z.object({
    created: z.string().optional(),
    updated: z.string().optional(),
    firstPublished: z.string().optional(),
    published: z.string().optional()
  }),
  characteristics: z.object({
    lifetime: z.number(),
    hotness: z.number()
  }),
  clientProperties: z.object({
    language: z.string()
  }),
  access: accessControlSchema,
  authors: z.array(authorEntitySchema)
});
var embeddedComponentSchema = embedResolverResponseSchema.omit({ type: true }).extend({
  ...baseComponentSchema.shape,
  url: z.string(),
  type: z.literal("integration-enrichment")
});
var questionStatisticsSchema = z.object({
  answers: z.record(z.string()),
  questionId: z.string(),
  lastUpdated: z.number(),
  answer: z.string()
}).partial();
var pollComponentSchema = questionPresentationPropertiesSchema.extend({
  type: z.literal("poll"),
  id: z.string(),
  question: z.string(),
  options: z.array(z.string()),
  image: imageComponentSchema.optional(),
  fact: textComponentSchema.optional(),
  statistics: questionStatisticsSchema.optional()
});
var vinkBaseSchema = z.object({
  id: z.string(),
  src: z.string().optional(),
  title: z.string(),
  slug: z.string(),
  section: z.string(),
  tags: z.array(tagSchema),
  hasGroups: z.boolean(),
  articleTemplate: articleTemplateSchema,
  hotness: z.number(),
  grade: gradeScoreSchema.optional(),
  colorPalette: colorPaletteSchema.optional(),
  teaserWithIllustration: z.boolean().optional(),
  numbered: z.boolean().optional(),
  socialMediaTitle: z.string().optional(),
  socialMediaImage: imageAssetSchema.nullable().optional(),
  description: z.string().optional()
});

// src/schemas/common/frapi/vink/teaser.ts
var accessControlSchema2 = z.object({
  restrictions: z.array(restrictionTypeSchema),
  control: z.object({
    aid: z.string(),
    productIds: z.string(),
    paywallType: z.enum([
      "",
      "subscription",
      "metered,subscription",
      "login",
      "login-metered,subscription"
    ])
  }).optional()
});
var vinkStoryTagSchema = z.object({
  id: z.string(),
  title: z.string(),
  background: z.string().optional()
});
var vinkTeaserSchema = vinkBaseSchema.extend({
  type: z.literal("teaser"),
  kicker: z.string().optional(),
  imageId: z.string().optional(),
  image: imageAssetSchema.optional(),
  storyTag: vinkStoryTagSchema.optional(),
  fullWidthTeaser: z.boolean().optional(),
  publicationDate: z.string(),
  priceLevel: priceLevelSchema.optional(),
  access: accessControlSchema2,
  lead: z.string().optional()
});

// src/schemas/common/frapi/vink/components/vink-teaser-component.ts
var vinkTeaserComponentSchema = baseComponentSchema.extend({
  ...vinkTeaserSchema.shape
});

// src/schemas/common/frapi/vink/components/byfunn-component.ts
var byfunnComponentSchema = z.union([
  ...componentsWithoutArticleSchema,
  vinkTeaserComponentSchema,
  embeddedComponentSchema,
  pollComponentSchema
]);
var sharedSearchTeaserSchema = z.object({
  id: z.string(),
  title: z.string(),
  url: z.string(),
  category: z.string(),
  paywallState: z.enum(["open", "closed"]),
  publicationDate: z.string(),
  updateDate: z.string().optional(),
  imageId: z.string(),
  authors: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      slug: z.string(),
      imageAsset: z.object({
        id: z.string(),
        url: z.string()
      }).optional()
    })
  ).optional()
});

// src/schemas/common/frapi/vink/promo.ts
var vinkPromoSchema = vinkBaseSchema.extend({
  published: z.string().optional(),
  updated: z.string().optional(),
  authors: z.array(authorEntitySchema),
  restaurantTitle: z.string().optional(),
  restaurantAddress: z.string().optional(),
  grades: gradesSchema.optional(),
  factBox: richTextSchema.optional(),
  lead: leadTextComponentSchema.optional(),
  mainImage: imageAssetSchema.optional(),
  metaDescription: z.string().optional(),
  previewText: z.array(textBlockSchema).optional(),
  wordCount: z.number(),
  googleMapsLink: z.string().optional(),
  priceLevel: priceLevelSchema.optional(),
  restaurant_url: z.string().optional(),
  pageTitle: z.string().optional()
});

// src/schemas/common/frapi/vink/article.ts
var accessControlSchema3 = z.object({
  restrictions: z.array(restrictionTypeSchema),
  control: z.object({
    aid: z.string(),
    productIds: z.string(),
    paywallType: z.enum([
      "",
      "subscription",
      "metered,subscription",
      "login",
      "login-metered,subscription"
    ])
  }).optional()
});
var vinkArticleSchema = vinkPromoSchema.extend({
  components: z.array(byfunnComponentSchema),
  access: accessControlSchema3
});
var byfunnCapiArticleSchema = articleBaseSchema.omit({
  customProperties: true
}).merge(z.object({ customProperties: legacyCustomPropertiesSchema }));
var articleIdParamSchema = z.string().regex(
  /^[0-9a-zA-Z]+$/,
  "Article ID must contain only alphanumeric characters"
).min(6).max(6).describe("Article Id (6 character alphanumeric string)");
var articleIdsParam = z.string().transform((ids) => {
  const idArray = ids.split(",");
  return idArray.map((id) => id.trim());
}).refine(
  (ids) => ids.every((id) => articleIdParamSchema.safeParse(id).success),
  "Article IDs must be comma-separated 6-character alphanumeric strings"
).describe("Comma-separated string of article Ids");
var curateContextParamSchema = z.string().min(1, "Curate context is required").describe("Page context used by Blender API");
var idOrSlugParamSchema = z.string().min(1, "Id or slug is required").describe("Id or slug");
var idsOrSlugsTagParamSchema = z.string().transform((ids) => ids.replaceAll(" ", "")).refine(
  (ids) => ids.split(/[,|]/).every((id) => idOrSlugParamSchema.safeParse(id).success),
  {
    message: "Must be a pipe or comma-separated string of valid IDs or slugs."
  }
).describe("Pipe or comma-separated string of IDs or slugs");
var idsOrSlugsSectionParamSchema = z.string().transform((ids) => {
  const idArray = ids.split("|");
  return idArray.map((id) => id.trim());
}).refine(
  (ids) => ids.every((id) => idOrSlugParamSchema.safeParse(id).success)
).describe("Pipe-separated string of IDs or slugs");
var listIdParamSchema = z.string().min(1, "List id is required").describe("CAPI list id");

// src/schemas/common/utils.ts
var ValidationError = class extends Error {
  code = 400;
  numberOfIssues = 0;
  first3issues;
  constructor(message, zodError) {
    super(message);
    this.name = "ValidationError";
    this.numberOfIssues = zodError.issues.length;
    zodError.issues = zodError.issues.slice(0, 3);
    this.first3issues = zodError;
  }
};
var findClosestMatch = (input, allowedValues) => {
  if (!input) return null;
  const closest = allowedValues.reduce(
    (best, current) => {
      if (current.startsWith(input)) return current;
      if (input.startsWith(current)) return current;
      return best;
    },
    null
  );
  return closest;
};

// src/schemas/common/path-params/consts.ts
var AP = "ap";
var BT = "bt";
var SA = "sa";
var BYFUNN = "byfunn";
var BT_BENEFITS = "btfordel";
var SA_BENEFITS = "safordel";
var AP_BENEFITS = "apfordel";
var SITES = [
  AP,
  BT,
  SA,
  BYFUNN,
  BT_BENEFITS,
  SA_BENEFITS,
  AP_BENEFITS
];

// src/schemas/common/path-params/site/schema.ts
var siteParamSchema = z.string().refine(
  (site) => Object.values(SITES).includes(site),
  (val) => ({
    message: "Invalid site name",
    code: "invalid_site_parameter",
    params: {
      received: val,
      allowedSites: Object.values(SITES),
      suggestion: findClosestMatch(val, Object.values(SITES))
    }
  })
).describe("Site name");
var slugParamSchema = z.string().min(1).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Invalid slug format").describe("Slug");
var userIdParamSchema = z.string().min(1, "User id is required").describe("User id");
function createPipeDelimitedIdsSchema(queryParam) {
  return z.union([z.string(), z.array(z.string())]).transform((val) => typeof val === "string" ? [val] : val).transform(
    (values) => values.map((value) => {
      const ids = value.split("|").map((id) => id.trim());
      const hasNegativeFilter = ids.some((id) => id.startsWith("!"));
      return { ids, hasNegativeFilter };
    })
  ).refine(
    (items) => items.every(
      (item) => !(item.hasNegativeFilter && item.ids.length > 1)
    ),
    {
      message: `Do not use OR ('|') in a single ${queryParam} parameter if it contains a negation ('!').`
    }
  ).transform((items) => items.map((item) => item.ids)).describe("Pipe-separated list of ids.");
}

// src/schemas/common/query-params/author-ids/schema.ts
var authorIdsParamSchema = createPipeDelimitedIdsSchema("author-ids");
var emailDomainParamSchema = z.string().regex(/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, {
  message: "Invalid email domain format. The domain must contain non-zero name string, at least one dot, and at least two characters in TLD part (the last part after the dot)"
}).describe("Email domain");
var searchPhraseParamSchema = z.string().transform((value) => {
  let trimmed = value.trim();
  if (trimmed.length > 30) {
    trimmed = trimmed.substring(0, 30);
  }
  if (trimmed.length > 20) {
    const lastSpaceIndex = trimmed.substring(0, 20).lastIndexOf(" ");
    if (lastSpaceIndex > 0) {
      trimmed = trimmed.substring(0, lastSpaceIndex);
    }
  }
  return trimmed;
}).refine(
  (value) => {
    const words = value.split(/\s+/).filter(Boolean);
    const wildcardWords = words.filter((word) => word.includes("*"));
    return wildcardWords.length <= 1;
  },
  {
    message: 'Wildcards (*) can only be used within a single word. Examples: "ibra*" or "*ibra*", but not "zlat* ibra*", or "a* b*"'
  }
);
function createIntegerParamSchema(queryParam, describe = "", allowZero = false) {
  return z.string().nonempty({ message: `${queryParam} cannot be empty` }).transform((val) => Number(val)).refine((num) => Number.isInteger(num), {
    message: `${queryParam} must be an integer`
  }).refine((num) => allowZero ? num >= 0 : num > 0, {
    message: allowZero ? `${queryParam} must be a non-negative integer` : `${queryParam} must be a positive integer`
  }).describe(describe);
}

// src/schemas/common/query-params/from/schema.ts
var fromParamSchema = createIntegerParamSchema(
  "from",
  "Pagination: first index to return",
  true
);
var hotnessParamSchema = z.string().regex(
  /^\d+\.\.\d+$/,
  'Hotness parameter must be in format: min..max (e.g. "0..9", "10..90")'
);

// src/schemas/common/query-params/page/schema.ts
var pageParamSchema = createIntegerParamSchema(
  "page",
  "Page number for pagination"
);
var sectionIdsParamSchema = z.string().refine((value) => value !== "" && value !== "|", {
  message: "Section ids cannot be empty or just a delimiter"
}).transform((value) => value.split("|")).refine((array) => array.every((item) => item !== ""), {
  message: "All section ids must be non-empty"
}).describe("Pipe-separated list of section ids.");

// src/schemas/common/query-params/size/schema.ts
var sizeParamSchema = createIntegerParamSchema(
  "size",
  "Number of items on one page. Default value : 10"
);

// src/schemas/common/query-params/source-ids/schema.ts
var sourceIdsParamSchema = createPipeDelimitedIdsSchema("source-ids");

// src/schemas/common/query-params/tag-ids/schema.ts
var tagIdsParamSchema = createPipeDelimitedIdsSchema("tag-ids");
var tagTypesParamSchema = z.string().transform((str) => str.split("|")).refine(
  (values) => values.every((value) => tagTypesSchema.safeParse(value).success),
  {
    message: "All tag types must be valid"
  }
).transform((values) => values.map((value) => tagTypesSchema.parse(value))).optional();

// src/schemas/common/query-params/to/schema.ts
var toParamSchema = createIntegerParamSchema(
  "to",
  "Pagination: last index to return"
);
var nullSchema = z.object({}).describe("Null schema");
var validate = async (schema, data, errorMessage) => {
  try {
    return await schema.parseAsync(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new ValidationError(errorMessage, error);
    }
    throw error;
  }
};
var validateQuery = async (schema, request) => validate(schema, request.query, "Invalid query parameters");
var validateParams = async (schema, request) => validate(schema, request.params, "Invalid path parameters");

// src/schemas/common/with-parsed-request.ts
var withParsedRequest = (pathSchema, querySchema, handler) => {
  return async (request, response) => {
    const path = await validateParams(pathSchema, request);
    const query = await validateQuery(querySchema, request);
    return handler({ path, query }, request, response);
  };
};

// src/schemas/author/schema.ts
var authorSchema = z.object({
  id: z.string().min(1, "ID is missing"),
  title: z.string().min(1, "Title is missing"),
  description: z.string().min(1, "Description is missing"),
  contacts: z.array(contactDetailSchema),
  slug: z.string().min(1, "Slug is missing"),
  enabled: z.boolean(),
  scope: z.enum(SITES)
});
var authorResponseSchema = authorSchema;
var authorPathSchema = z.object({
  site: siteParamSchema,
  idOrSlug: idOrSlugParamSchema
});
var validateAuthor = (data) => {
  try {
    authorSchema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new ValidationError(
        "Author does not match the expected schema",
        error
      );
    }
    throw error;
  }
};
var validateAuthorResponse = validateAuthor;
var authorWithTitleSchema = authorEntitySchema.extend({
  workingTitle: z.string()
});
var journalistResponseSchema = z.object({
  type: z.literal("journalist"),
  author: authorWithTitleSchema,
  topTags: z.object({
    items: z.array(baseTagSchema)
  })
});
var authorsCollectionResponseSchema = z.object({
  type: z.literal("authors-collection"),
  authors: z.array(authorWithTitleSchema),
  topTags: z.object({
    items: z.array(baseTagSchema)
  })
});
var createAuthorPageResponseSchema = (articleSchema, type) => {
  const authorContentSchema = z.object({
    authorContent: z.object({
      articles: z.array(articleSchema),
      resultsCount: z.number(),
      pagesCount: z.number()
    })
  });
  if (type === "journalist") {
    return journalistResponseSchema.merge(
      authorContentSchema
    );
  } else {
    return authorsCollectionResponseSchema.merge(
      authorContentSchema
    );
  }
};
var authorPagePathSchema = z.object({
  site: siteParamSchema,
  slug: slugParamSchema
});
var authorPageQuerySchema = z.object({
  emailDomain: emailDomainParamSchema.optional(),
  size: sizeParamSchema.optional(),
  authorSize: sizeParamSchema.optional(),
  page: pageParamSchema.optional(),
  sort: sortFieldSchema.optional(),
  tagTypes: tagTypesParamSchema.optional(),
  tagLimit: sizeParamSchema.optional(),
  tagIds: tagIdsParamSchema.optional()
});
var validateAuthorPageResponse = (data, articleSchema, type) => {
  try {
    const authorPageResponseSchema = createAuthorPageResponseSchema(
      articleSchema,
      type
    );
    authorPageResponseSchema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new ValidationError(
        "Author collection page response does not match the expected schema",
        error
      );
    }
    throw error;
  }
};
var articleIdPreviewSchema = z.string().regex(
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}@preview$/i,
  'Article ID must be a valid UUID followed by "@preview"'
).describe('Article Id (UUID string with "@preview" suffix)');

// src/schemas/article/schema.ts
var articlePathSchema = z.object({
  site: siteParamSchema,
  articleId: z.union([articleIdParamSchema, articleIdPreviewSchema])
});
var validateArticleResponse = (data, entitySchema) => {
  try {
    entitySchema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new ValidationError(
        "Article response does not match the expected schema",
        error
      );
    }
    throw error;
  }
};
var blenderQuerySchema = z.object({
  id: z.string().optional().describe("Specific article ID to include"),
  userId: z.string().optional().describe("User ID for personalization"),
  origin: z.string().optional().describe("Origin of the request"),
  subscriber: z.enum(["true", "false"]).optional().describe("Whether the user is a subscriber"),
  userType: z.enum(["loggedin", "anonymous"]).optional().describe("Type of user making the request"),
  limit: z.number().int().positive().optional().describe("Maximum number of teasers to return")
}).strict();
var blenderResponseWithIdsSchema = blenderResponseSchema.extend({
  ids: z.array(z.string())
});
var validateBlenderQuery = (data) => {
  try {
    blenderQuerySchema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new ValidationError(
        "Blender query parameters do not match the expected schema",
        error
      );
    }
    throw error;
  }
};
var validateBlenderResponse = (data) => {
  try {
    blenderResponseWithIdsSchema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new ValidationError(
        "Blender response does not match the expected schema",
        error
      );
    }
    throw error;
  }
};
var filterBlenderQueryParameters = (queryParams) => {
  const validKeys = Object.keys(blenderQuerySchema.shape);
  const filteredEntries = Object.entries(queryParams).filter(
    ([key]) => validKeys.includes(key)
  );
  return Object.fromEntries(filteredEntries);
};
var curateParamsSchema = z.object({
  site: siteParamSchema,
  curateContext: curateContextParamSchema
});
var curateQuerySchema = blenderQuerySchema.extend({
  format: z.enum(["iris", "article"]).optional().describe("Specifies the entity format for the returned data"),
  limit: z.string().transform((str) => parseInt(str, 10)).pipe(z.number().int().positive()).optional().describe("Maximum number of teasers to return")
});
var createCurateResponseSchema = (itemSchema) => {
  return z.object({
    items: listWithBundlesSchema(itemSchema),
    properties: blenderPropertiesSchema,
    personalization: blenderPersonalizationSchema
  });
};
var validateCurateParams = (data) => {
  try {
    return curateParamsSchema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new ValidationError(
        "Curate path parameters do not match the expected schema",
        error
      );
    }
    throw error;
  }
};
var validateCurateQuery = (data) => {
  try {
    return curateQuerySchema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new ValidationError(
        "Curate query parameters do not match the expected schema",
        error
      );
    }
    throw error;
  }
};
var validateCurateResponse = (data, itemSchema) => {
  try {
    const schema = createCurateResponseSchema(itemSchema);
    schema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new ValidationError(
        "Curate response does not match the expected schema",
        error
      );
    }
    throw error;
  }
};
var baseListResponseSchema = z.object({
  items: z.array(listItemSchema),
  hits: hitsInfoSchema,
  properties: z.record(z.unknown()).optional()
});
var createArticleListResponseSchema = (itemSchema) => z.object({
  items: z.array(itemSchema),
  hits: hitsInfoSchema,
  properties: z.record(z.unknown()).optional()
});
var listPathSchema = z.object({
  site: siteParamSchema,
  listId: listIdParamSchema
});
var listQuerySchema = z.object({
  size: sizeParamSchema.optional(),
  page: pageParamSchema.optional()
});
var validateListArticleResponse = (data, itemSchema) => {
  try {
    createArticleListResponseSchema(itemSchema).parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new ValidationError(
        "List response does not match the expected schema",
        error
      );
    }
    throw error;
  }
};
var validateBaseListResponse = (data) => {
  try {
    baseListResponseSchema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new ValidationError(
        "List response does not match the expected schema",
        error
      );
    }
    throw error;
  }
};
var promoParamsSchema = z.object({
  site: siteParamSchema,
  articleId: z.union([articleIdParamSchema, articleIdPreviewSchema])
});
var validatePromoResponse = (data, dataSchema) => {
  try {
    dataSchema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new ValidationError(
        "PromoResponse does not match the expected schema",
        error
      );
    }
    throw error;
  }
};
var createTeasersResponseSchema = (itemSchema) => z.object({
  items: z.array(itemSchema)
});
var teasersPathSchema = z.object({
  site: siteParamSchema,
  ids: articleIdsParam
});
var validateTeasersResponse = (data, itemSchema) => {
  try {
    createTeasersResponseSchema(itemSchema).parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new ValidationError(
        "Teasers response does not match the expected schema",
        error
      );
    }
    throw error;
  }
};
var recommendationsPathSchema = z.object({
  site: siteParamSchema,
  articleId: z.union([articleIdParamSchema, articleIdPreviewSchema])
});
var recommendationsQuerySchema = z.object({
  size: sizeParamSchema.optional(),
  page: pageParamSchema.optional(),
  hotness: hotnessParamSchema.optional()
});
var createRecommendationsResponseSchema = (itemSchema) => z.object({
  items: z.array(itemSchema)
});
var validateRecommendationsResponse = (data, itemSchema) => {
  try {
    createRecommendationsResponseSchema(itemSchema).parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new ValidationError(
        "Recommendations response does not match the expected schema",
        error
      );
    }
    throw error;
  }
};
var searchAuthorsPathSchema = z.object({
  site: siteParamSchema
});
var searchAuthorsQuerySchema = z.object({
  q: searchPhraseParamSchema.optional(),
  from: fromParamSchema.optional(),
  to: toParamSchema.optional(),
  size: sizeParamSchema.optional(),
  page: pageParamSchema.optional()
});
var searchAuthorsResponseSchema = z.object({
  items: authorItemSchema.array(),
  hits: hitsInfoSchema
});
var validateSearchAuthorsResponse = (data) => {
  try {
    searchAuthorsResponseSchema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new ValidationError(
        "Search authors response does not match the expected schema",
        error
      );
    }
    throw error;
  }
};
var createSearchResponseSchema = (teaserSchema) => z.object({
  resultsCount: z.number(),
  pagesCount: z.number(),
  articles: z.array(teaserSchema)
});
var searchPathSchema = z.object({
  site: siteParamSchema
});
var searchQuerySchema = z.object({
  phrase: z.string().optional(),
  sectionIds: sectionIdsParamSchema.optional(),
  tagIds: tagIdsParamSchema.optional(),
  authorIds: authorIdsParamSchema.optional(),
  sort: sortFieldSchema.optional(),
  size: sizeParamSchema.optional(),
  page: pageParamSchema.optional(),
  sourceIds: z.union([z.string(), z.array(z.string())]).optional()
});
var validateSearchResponse = (data, teaserSchema) => {
  try {
    const searchResponseSchema = createSearchResponseSchema(teaserSchema);
    searchResponseSchema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new ValidationError(
        "Search response does not match the expected schema",
        error
      );
    }
    throw error;
  }
};
var createSectionTeasersResponseSchema = (itemSchema) => z.object({
  info: z.object({
    section: z.array(sectionEntitySchema)
  }),
  items: z.array(itemSchema),
  hits: hitsInfoSchema
});
var sectionTeaserPathSchema = z.object({
  site: siteParamSchema,
  idsOrSlugs: idsOrSlugsSectionParamSchema
});
var sectionTeaserQuerySchema = z.object({
  size: sizeParamSchema.optional(),
  page: pageParamSchema.optional(),
  hotness: hotnessParamSchema.optional(),
  sourceIds: sourceIdsParamSchema.optional()
});
var validateSectionTeaserResponse = (data, itemSchema) => {
  try {
    const sectionTeasersResponseSchema = createSectionTeasersResponseSchema(itemSchema);
    sectionTeasersResponseSchema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new ValidationError(
        "Section teaser response does not match the expected schema",
        error
      );
    }
    throw error;
  }
};

// src/schemas/common/query-params/date-range-gte/schema.ts
var dateRangeGteSchema = createIntegerParamSchema(
  "dateRangeGte",
  "Date value for which the stored article should have its dateRange.field",
  false
);

// src/schemas/tag-teasers/schema.ts
var tagTeasersPathSchema = z.object({
  site: siteParamSchema,
  idOrSlugs: idsOrSlugsTagParamSchema
});
var tagTeasersQuerySchema = z.object({
  size: sizeParamSchema.optional(),
  page: pageParamSchema.optional(),
  hotness: hotnessParamSchema.optional(),
  sourceIds: sourceIdsParamSchema.optional(),
  dateRangeGte: dateRangeGteSchema.optional(),
  dateRangeField: dateRangeFieldSchema.optional()
});
var createTagTeasersResponseSchema = (itemSchema) => z.object({
  info: z.object({
    tags: z.array(baseTagSchema)
  }),
  items: z.array(itemSchema),
  hits: hitsInfoSchema
});
var validateTagTeasersResponse = (data, itemSchema) => {
  try {
    const tagTeasersResponseSchema = createTagTeasersResponseSchema(itemSchema);
    tagTeasersResponseSchema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new ValidationError(
        "Tag teasers response does not match the expected schema",
        error
      );
    }
    throw error;
  }
};
var createTeasersSearchResponseSchema = (itemSchema) => z.object({
  items: z.array(itemSchema),
  hits: z.object({
    total: z.number(),
    from: z.number(),
    size: z.number()
  })
});
var teasersSearchPathSchema = z.object({
  site: siteParamSchema
});
var teasersSearchQuerySchema = z.object({
  sectionIds: sectionIdsParamSchema.optional(),
  tagIds: tagIdsParamSchema.optional(),
  authorIds: authorIdsParamSchema.optional(),
  size: sizeParamSchema.optional(),
  page: pageParamSchema.optional(),
  hotness: hotnessParamSchema.optional(),
  sourceIds: sourceIdsParamSchema.optional()
});
var validateTeasersSearchResponse = (data, itemSchema) => {
  try {
    createTeasersSearchResponseSchema(itemSchema).parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new ValidationError(
        "Teasers search response does not match the expected schema",
        error
      );
    }
    throw error;
  }
};

// src/schemas/top-tags/consts.ts
var PHRASE_BEST_MATCH_SORT = "phraseBestMatch";
var SORTING_OPTIONS = [PHRASE_BEST_MATCH_SORT];

// src/schemas/top-tags/schema.ts
var topTagResponseSchema = z.object({
  items: z.array(tagSchema)
});
var topTagsParamsSchema = z.object({
  site: siteParamSchema
});
var topTagsQuerySchema = z.object({
  sectionIds: sectionIdsParamSchema.optional(),
  authorIds: authorIdsParamSchema.optional(),
  tagTypes: tagTypesParamSchema.optional(),
  limit: z.string().transform((str) => parseInt(str, 10)).pipe(z.number().int().positive().max(100)).optional().default("30"),
  phrase: z.string().optional(),
  sort: z.enum(SORTING_OPTIONS).optional()
});
var validateTag = (data) => {
  try {
    tagSchema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new ValidationError(
        "Tag does not match the expected schema",
        error
      );
    }
    throw error;
  }
};
var validateTopTagResponse = (data) => {
  try {
    topTagResponseSchema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new ValidationError(
        "TopTagResponse does not match the expected schema",
        error
      );
    }
    throw error;
  }
};
var storyInfoResponseSchema = storyEntitySchema;
var storyInfoPathSchema = z.object({
  site: siteParamSchema,
  idOrSlug: idOrSlugParamSchema
});
var validateStoryInfoResponse = (data) => {
  try {
    storyInfoResponseSchema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new ValidationError(
        "Story info response does not match the expected schema",
        error
      );
    }
    throw error;
  }
};
var storyTeasersQuerySchema = z.object({
  page: pageParamSchema.optional(),
  size: sizeParamSchema.optional()
});
var createStoryTeasersResponseSchema = (itemSchema) => z.object({
  info: z.object({
    story: storyEntitySchema
  }),
  items: z.array(itemSchema),
  hits: hitsInfoSchema
});
var storyTeasersPathSchema = z.object({
  site: siteParamSchema,
  idOrSlug: idOrSlugParamSchema
});
var validateStoryTeasersResponse = (data, itemSchema) => {
  try {
    const storyTeasersResponseSchema = createStoryTeasersResponseSchema(itemSchema);
    storyTeasersResponseSchema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new ValidationError(
        "Story teasers response does not match the expected schema",
        error
      );
    }
    throw error;
  }
};
var tagInfoResponseSchema = tagSchema;
var tagInfoPathSchema = z.object({
  site: siteParamSchema,
  idOrSlug: idOrSlugParamSchema
});
var validateTagInfoResponse = (data) => {
  try {
    tagInfoResponseSchema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new ValidationError(
        "Tag info response does not match the expected schema",
        error
      );
    }
    throw error;
  }
};
var historyLastReadPathSchema = z.object({
  site: siteParamSchema,
  userId: userIdParamSchema
});
var historyLastReadQuerySchema = z.object({
  limit: z.string().transform((str) => parseInt(str, 10)).pipe(z.number().int().positive().max(100)).optional().default("30")
});

// src/utils/make-schema-for-article-preview.ts
var makeSchemaForArticlePreview = (zodSchema) => zodSchema.partial().extend({ id: articleIdPreviewSchema });

export { AP, AP_BENEFITS, BT, BT_BENEFITS, BYFUNN, ListBlockTypes, MarkupOptions, PHRASE_BEST_MATCH_SORT, SA, SA_BENEFITS, SITES, SORTING_OPTIONS, TextBlockTypes, ValidationError, accessControlSchema, aggregatedItemSchema, benefitsArticleSchema as apBenefitsArticleSchema, benefitsPromoSchema as apBenefitsPromoSchema, apBenefitsTeaserSchema, legacyCapiArticleSchema as apCapiArticleSchema, apTeaserSchema, articleBaseSchema, articleCharacteristicsSchema, articleComponentSchema, articleEnrichmentsSchema, articleIdParamSchema, articleIdsParam, articleListItemSchema, articlePathSchema, articleTemplateSchema, authorEntitySchema, authorIdsParamSchema, authorItemSchema, authorPagePathSchema, authorPageQuerySchema, authorPathSchema, authorResponseSchema, authorSchema, baseComponentPresentationPropertiesSchema, baseComponentSchema, baseListResponseSchema, baseTagSchema, baseTeaserSchema, benefitsArticleSchema, benefitsBaseSchema, benefitsCapiArticleSchema, benefitsCustomPropertiesSchema, benefitsPresentationPropertiesSchema, benefitsPromoSchema, benefitsSearchTeaserSchema, benefitsTeaserSchema, blenderArticleItemSchema, blenderBundleItemSchema, blenderItemSchema, blenderPersonalizationSchema, blenderPropertiesSchema, blenderQuerySchema, blenderResponseSchema, blenderResponseWithIdsSchema, breakpointComponentSchema, benefitsArticleSchema as btBenefitsArticleSchema, benefitsPromoSchema as btBenefitsPromoSchema, btBenefitsTeaserSchema, btCapiArticleSchema, btCustomPropertiesSchema, btPresentationPropertiesSchema, btPromotionPropertiesSchema, btTeaserSchema, bundleSchema, byfunnCapiArticleSchema, byfunnComponentSchema, legacyCustomPropertiesSchema as byfunnCustomPropertiesSchema, changesSchema, characteristicsRangeSchema, colorPaletteSchema, commonCustomPropertiesSchema, componentSchema, componentsWithoutArticleSchema, contactDetailSchema, contactTypeSchema, correctionComponentSchema, createArticleListResponseSchema, createAuthorPageResponseSchema, createCurateResponseSchema, createRecommendationsResponseSchema, createSearchResponseSchema, createSectionTeasersResponseSchema, createStoryTeasersResponseSchema, createTagTeasersResponseSchema, createTeasersResponseSchema, createTeasersSearchResponseSchema, curateContextParamSchema, curateContextSchema, curateParamsSchema, curateQuerySchema, dateRangeFieldSchema, descriptorTagSchema, emailDomainParamSchema, embedResolverResponseSchema, embeddedComponentSchema, entityMapSchema, eventTagSchema, experimentSchema, factComponentSchema, fetchEntityTypesSchema, filterBlenderQueryParameters, findClosestMatch, fromParamSchema, frontpageGalleryAssetSchema, galleryComponentSchema, gradeComponentSchema, gradeSchema, gradeScoreSchema, gradeWithScoreSchema, gradesSchema, historyLastReadPathSchema, historyLastReadQuerySchema, hitsInfoSchema, hotnessParamSchema, hotspotSchema, idOrSlugParamSchema, identifiableSchema, idsOrSlugsSectionParamSchema, idsOrSlugsTagParamSchema, imageAssetSchema, imageAssetWithUrlsSchema, imageComponentSchema, imageSchema, imageUrlSchema, imageWithUrlSchema, integrationComponentSchema, interestTagSchema, invitererPropsSchema, irisTeaserSchema, isGradeScore, isGradeWithScore, isLinkMarkup, isListBlock, isSymbolMarkup, isTextBlock, knownArticlePresentationPropertiesSchema, leadTextComponentSchema, legacyCapiArticleSchema, legacyCustomPropertiesSchema, legacyPresentationPropertiesSchema, legacyPromotionPropertiesSchema, listBlockSchema, listComponentSchema, listIdParamSchema, listItemSchema, listItemTypeSchema, listPathSchema, listQuerySchema, listSchema, listWithBundlesSchema, locationMetadataSchema, locationTagSchema, machineTagSchema, makeSchemaForArticlePreview, mapComponentSchema, markupSchema, ntbMetadataSchema, nullSchema, organizationMetadataSchema, organizationTagSchema, pageParamSchema, paywallTypeSchema, personTagSchema, pollComponentSchema, priceLevelSchema, promoParamsSchema, promotionContentSchema, pulseTrackingSchema, questionComponentSchema, questionPresentationPropertiesSchema, quoteComponentSchema, recommendationsPathSchema, recommendationsQuerySchema, restrictionTypeSchema, richTextSchema, benefitsArticleSchema as saBenefitsArticleSchema, benefitsPromoSchema as saBenefitsPromoSchema, saBenefitsTeaserSchema, legacyCapiArticleSchema as saCapiArticleSchema, baseTeaserSchema as saTeaserSchema, searchAuthorsPathSchema, searchAuthorsQuerySchema, searchAuthorsResponseSchema, searchPathSchema, searchPhraseParamSchema, searchQuerySchema, sectionEntitySchema, sectionIdsParamSchema, sectionItemSchema, sectionTeaserPathSchema, sectionTeaserQuerySchema, sharedSearchTeaserSchema, siteParamSchema, sizeParamSchema, slugParamSchema, sortFieldSchema, sourceIdsParamSchema, sourceSchema, specificPriceLevelSchema, storyEntitySchema, storyInfoPathSchema, storyInfoResponseSchema, storyItemSchema, storyTeasersPathSchema, storyTeasersQuerySchema, subheadingComponentSchema, svpVideoAssetSchema, tableComponentSchema, tagIdsParamSchema, tagInfoPathSchema, tagInfoResponseSchema, tagItemSchema, tagSchema, tagTeasersPathSchema, tagTeasersQuerySchema, tagTypesParamSchema, tagTypesSchema, teaserVariantSchema, teaserVariantsConfigurationSchema, teasersPathSchema, teasersSearchPathSchema, teasersSearchQuerySchema, textBlockSchema, textComponentSchema, textSchema, textToSpeechEnrichmentSchema, toParamSchema, topTagResponseSchema, topTagsParamsSchema, topTagsQuerySchema, urlComponentSchema, userIdParamSchema, utcDateTimeStringSchema, validateArticleResponse, validateAuthor, validateAuthorPageResponse, validateAuthorResponse, validateBaseListResponse, validateBlenderQuery, validateBlenderResponse, validateCurateParams, validateCurateQuery, validateCurateResponse, validateListArticleResponse, validateParams, validatePromoResponse, validateQuery, validateRecommendationsResponse, validateSearchAuthorsResponse, validateSearchResponse, validateSectionTeaserResponse, validateStoryInfoResponse, validateStoryTeasersResponse, validateTag, validateTagInfoResponse, validateTagTeasersResponse, validateTeasersResponse, validateTeasersSearchResponse, validateTopTagResponse, videoAssetSchema, videoComponentSchema, videoEntitySchema, videoIntervalSchema, videoSectionSchema, vinkArticleSchema, vinkBaseSchema, vinkPromoSchema, vinkStoryTagSchema, vinkTeaserComponentSchema, vinkTeaserSchema, websiteMetadataSchema, withParsedRequest };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map